import { Component, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';

export interface MetadataSetupDialogData {
  projectId: string;
  defaultProjectName?: string;
}

export interface MetadataSetupResult {
  success: boolean;
  projectName?: string;
  projectDescription?: string;
  error?: string;
}

/**
 * Dialog for setting up metadata for legacy projects (Issue #227 - Story 3).
 *
 * Prompts user to enter project name and description.
 * Pre-fills project name with project ID.
 * Calls CreateProjectMetadata RPC.
 */
@Component({
  selector: 'app-metadata-setup-dialog',
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatIconModule,
  ],
  template: `
    <h2 mat-dialog-title>Set Up Project Metadata</h2>

    <mat-dialog-content>
      <p class="dialog-description">Add a project name and description to help identify and organize this project.</p>

      @if (saving) {
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      }

      <div class="form-fields" [class.disabled]="saving">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Project Name</mat-label>
          <input
            matInput
            [(ngModel)]="projectName"
            [disabled]="saving"
            placeholder="Enter a descriptive name for this project"
            (keyup.enter)="onSave()"
          />
          <mat-hint>A short, descriptive name (e.g., "Downtown Office Building")</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Project Description (Optional)</mat-label>
          <textarea
            matInput
            [(ngModel)]="projectDescription"
            [disabled]="saving"
            rows="3"
            placeholder="Add additional details about this project"
          ></textarea>
          <mat-hint>Provide context, location, or other relevant details</mat-hint>
        </mat-form-field>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()" [disabled]="saving">Cancel</button>
      <button mat-raised-button color="primary" (click)="onSave()" [disabled]="saving || !isValid()">
        @if (!saving) {
          <mat-icon>save</mat-icon>
        }
        @if (saving) {
          <mat-icon class="spinning">hourglass_empty</mat-icon>
        }
        {{ saving ? 'Saving...' : 'Save Metadata' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .dialog-description {
        margin-bottom: 24px;
        color: rgba(0, 0, 0, 0.6);
      }

      .form-fields {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-top: 16px;
      }

      .form-fields.disabled {
        opacity: 0.6;
        pointer-events: none;
      }

      .full-width {
        width: 100%;
      }

      mat-dialog-content {
        min-width: 500px;
        max-width: 600px;
      }

      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }

      .spinning {
        animation: spin 1s linear infinite;
      }
    `,
  ],
})
export class MetadataSetupDialogComponent {
  dialogRef = inject<MatDialogRef<MetadataSetupDialogComponent>>(MatDialogRef);
  data = inject<MetadataSetupDialogData>(MAT_DIALOG_DATA);

  projectName: string;
  projectDescription: string = '';
  saving: boolean = false;

  constructor() {
    const data = this.data;

    // Pre-fill project name with project ID (user can edit)
    this.projectName = data.defaultProjectName || data.projectId;
  }

  isValid(): boolean {
    return this.projectName.trim().length > 0;
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  async onSave(): Promise<void> {
    if (!this.isValid() || this.saving) {
      return;
    }

    this.saving = true;

    // Return data for parent component to handle the RPC
    // (keeps dialog UI-only, business logic in parent)
    this.dialogRef.close({
      success: true,
      projectName: this.projectName.trim(),
      projectDescription: this.projectDescription.trim(),
    });
  }
}
